import { Clipboard } from '@angular/cdk/clipboard';
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { PendoService } from 'analytics-lib';
import { BullhornAuthService } from 'auth-service-lib';
import { jwtDecode } from 'jwt-decode';
import { MessageNotificationService } from 'message-notification-service-lib';
import moment from 'moment';
import { IdentityStorage } from 'storage-service-lib';
import { environment } from '../../../environments/environment';
import { CallDurationService } from '../../services/call-duration.service';
import { ConfigService } from '../../services/config.service';
import { NotetakerCallsService } from '../../services/notetaker-calls.service';
import { AIApps } from '../../types/AIApps';
import { IBullhornCardContext } from '../../types/bullhorn-card-context';
import { IClientConfig } from '../../types/client-config';
import { IJointCall } from '../notetaker-card/notetaker-result/ICallInterface';
import { NoteTakerGlobalSettingsComponent } from './note-taker-global-settings/note-taker-global-settings.component';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export interface IBillingPeriod {
  name: string;
  label: string;
  period: string;
}

@Component({
    selector: 'app-notetaker-manager',
    templateUrl: './notetaker-manager.component.html',
    styleUrl: './notetaker-manager.component.scss',
    standalone: false,
    providers: [
      // Moment can be provided globally to your app by adding `provideMomentDateAdapter`
      // to your app config. We provide it at the component level here, due to limitations
      // of our example generation script.
      provideMomentDateAdapter(MY_FORMATS),
    ],
    encapsulation: ViewEncapsulation.None,
    // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotetakerManagerComponent implements OnInit {
  readonly billingPeriodControl = new FormControl(this.billingPeriod.label);
  protected bullhornCardContext!: IBullhornCardContext;
  public durationSearchRangeStart: Date = new Date(moment().year(), moment().month(), 1);
  public durationSearchRangeEnd: Date = new Date();
  public billingPeriods: IBillingPeriod[] = [];

  callsDisplayedColumns: string[] = [
    'callTitle',
    'callerId',
    'timeDate',
    'attendeeEmail',
    'bhEntityIds',
  ];

  callsDataSource: any[] = [];
  corpIdDisplay = '';
  searchText = '';
  protected clientConfig!: IClientConfig;
  filteredCallsDataSource = new MatTableDataSource<any>();
  totalHours = 0;
  totalCalls = 0;

  expandVersion = false;
  protected readonly appVersion: string;
  protected readonly environment = environment;

  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('clearSearch') clearSearch!: ElementRef;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    public router: Router,
    private identityStorage: IdentityStorage,
    private dialog: MatDialog,
    private bullhornAuthService: BullhornAuthService,
    private noteTakerCallsService: NotetakerCallsService,
    private clientConfigService: ConfigService,
    private messageNotificationService: MessageNotificationService,
    private clipboard: Clipboard,
    private pendoService: PendoService,
    private callDurationService: CallDurationService
  ) {
    this.appVersion = environment.version;
  }

  ngOnInit(): void {
    this.getUserCalls();
    this.loadPendo();
    this.refreshCallDuration();
  }

  public refreshCallDuration() {
    this.callDurationService.getCallDurationForPeriod(this.durationSearchRangeStart, this.durationSearchRangeEnd)
      .subscribe((it) => {
        this.totalCalls = it.numberOfCalls;
        this.totalHours = it.totalDuration;
      });
  }

  get billingPeriod(): any {
    const monthTracker = new Date(moment().year(), moment().month(), 1);
    const date = monthTracker.toLocaleDateString(environment.dateFormat, {
      month: 'long',
      year: 'numeric'
    });
    return this.buildBillingPeriod(monthTracker, date);
  }

  get formattedDuration(): string {
    const totalMinutes = Math.round(this.totalHours / 60);
    if (totalMinutes < 60) {
      return `${totalMinutes} min`;
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return minutes > 0 ? `${hours} hr(s) ${minutes} min` : `${hours} hr(s)`;
  }

  setMonthAndYear(event: any) {
    const date = new Date(event.value);
    this.durationSearchRangeStart = date;
    this.durationSearchRangeEnd = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    this.refreshCallDuration();
    this.loadUserCalls();
  }

  getOrdinal(num: number): string {
    const s = ['th', 'st', 'nd', 'rd'],
      v = num % 100;
    return num + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  private loadPendo() {
    this.pendoService.initPendoScript(environment.ntManagerPendoAPIKey);
  }

  // todo This will change
  private getUserCalls() {
    const storedIdentity = this.identityStorage.get();

    if (!storedIdentity) {
      this.bullhornAuthService.getToken().subscribe(() => {
        this.loadUserCalls();
      });
      return;
    }

    this.loadUserCalls();
  }

  private loadUserCalls() {
    const { corpId, namespaceId } = this.getCorpDetails();
    this.corpIdDisplay = corpId;

    this.clientConfigService
      .getFullConfigForCorpId(
        AIApps.NOTETAKER,
        environment.ntApiGatewayUrl,
        environment.dtApiGatewayUrl,
        namespaceId
      )
      .subscribe({
        next: (value) => {
          this.clientConfig = value;
          if (!this.clientConfig.notetakerEnabled) {
            this.messageNotificationService.error('Client is not enabled');
            return;
          }
          this.noteTakerCallsService.getCallsByCorpAndDate(
            namespaceId,
            this.durationSearchRangeStart,
            this.durationSearchRangeEnd
          ).subscribe({
            next: (calls: IJointCall[]) => {
              this.callsDataSource = this.filterRecentCalls(calls)
                .map(this.mapCallData)
                .sort((a, b) => b.timeDate.getTime() - a.timeDate.getTime()); // Sorting newest first

              this.filteredCallsDataSource = new MatTableDataSource(this.callsDataSource);
              this.filteredCallsDataSource.sort = this.sort;
              this.filteredCallsDataSource.sortingDataAccessor = (data, sortHeaderId) => {
                    const sortValue = data[sortHeaderId];
                    return typeof sortValue === 'string' ? sortValue.toUpperCase() : sortValue;
              };
              this.buildBillingPeriods(this.clientConfig.notetakerEnabledDate);
            },
            error: (err) => console.error('Error fetching calls:', err),
          });
        },
        error: (err) => console.error('Error fetching client config:', err),
      });
  }

  // todo put date in sort key in database - future ticket
  private getCorpDetails(): { corpId: string; namespaceId: string } {
    const storedJson = this.identityStorage.get();
    const jwt = jwtDecode<any>(storedJson?.token || '');
    const corpId = jwt.corporationId || '';
    const namespaceId = `bullhorn-${corpId}`;
    return { corpId, namespaceId };
  }

  private buildBillingPeriods(startDate: string | null): void {
    const billingStartDate = this.determineBillingStartDate(startDate);
    const currentDate = new Date();

    this.billingPeriods = this.generateBillingPeriodsList(
      billingStartDate,
      currentDate
    );
  }

  private determineBillingStartDate(startDate: string | null): Date {
    const dateString = startDate ?? environment.defaultBillingStartDate;
    const parsedDate = new Date(dateString);

    // Normalize to the first day of the month
    return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1);
  }

  private generateBillingPeriodsList(
    startDate: Date,
    endDate: Date
  ): IBillingPeriod[] {
    const periods: IBillingPeriod[] = [];
    const currentMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

    while (currentMonth >= startDate) {
      const monthYear = currentMonth.toLocaleDateString(environment.dateFormat, {
        month: 'long',
        year: 'numeric'
      });

      periods.push(this.buildBillingPeriod(currentMonth, monthYear));
      currentMonth.setMonth(currentMonth.getMonth() - 1);
    }

    return periods;
  }

  private buildBillingPeriod(
    monthDate: Date,
    formattedMonthYear: string
  ): IBillingPeriod {
    const isCurrentMonth = this.isCurrentMonth(monthDate);

    if (!isCurrentMonth) {
      return {
        name: formattedMonthYear,
        label: formattedMonthYear,
        period: ''
      };
    }

    // For the current month, show date range (e.g., "1st - 15th")
    const today = new Date().getDate();
    const periodText = today > 1
      ? `${this.getOrdinal(1)} - ${this.getOrdinal(today)}`
      : this.getOrdinal(1);

    return {
      name: formattedMonthYear,
      label: formattedMonthYear,
      period: ` (${periodText})`
    };
  }

  private isCurrentMonth(date: Date): boolean {
    const now = new Date();
    return date.getMonth() === now.getMonth() &&
           date.getFullYear() === now.getFullYear();
  }

  private filterRecentCalls(calls: IJointCall[]): IJointCall[] {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    return calls.filter((call) => new Date(call.date) >= ninetyDaysAgo);
  }

  private mapCallData = (call: IJointCall) => {
    return {
      callTitle: call.callTitle || '-',
      timeDate: new Date(call.date),
      formattedTimeDate: this.formatDate(call.date),
      bhEntityIds: call.entityIds.join(', '),
      callerId: call.caller,
      attendeeEmails: call.emails.join(', '),
    };
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString(environment.dateFormat, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true,
    });
  }

  filterCalls(event: Event) {
    this.filteredCallsDataSource.filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
  }

  clearSearchField() {
    this.searchText = '';
    this.filteredCallsDataSource.filter = '';
  }

  copyToClipboard(text: string): void {
    this.clipboard.copy(text);
    this.messageNotificationService.success(`Copied "${text}" to clipboard`);
  }

  openNoteTakerGlobalSettings() {
    this.dialog.open(NoteTakerGlobalSettingsComponent, {
      minHeight: '55vh',
      minWidth: '60vw',
      width: '60vw',
      autoFocus: false,
      data: this.getCorpDetails(),
    });
  }

  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    const searchInput = this.searchInput?.nativeElement;
    const clearSearch = this.clearSearch?.nativeElement;

    if (event.code === 'Escape' && this.clearSearch) {
      clearSearch.click();
    }

    if (this.dialog.openDialogs.length === 0 && (event.ctrlKey && event.shiftKey)) {
      if (event.code === 'KeyF') {
        searchInput.focus();
      }
    }

    if (event.altKey && event.shiftKey) {
      if (event.code === 'KeyV') {
        this.expandVersion = true;
        setTimeout(() => {
          this.expandVersion = false;
        }, 3000);
      }
    }
  }
}
